import React, { useState, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  Alert,
  Snackbar
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setMediaType } from "../../redux/slices/contentSlice/contentForm/contentForm";

const MediaTypeSelector = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  // Get current media type from Redux store
  const mediaType = useSelector((state) => state.contentForm.mediaType);
  
  // Get master file content if available
  const masterFileContent = useSelector((state) => state.contentForm.masterFileContent);

  // Validation for file types based on media selection
  const validateFileType = (mediaType, filePath) => {
    if (!filePath) return true; // No file selected yet
    
    const fileExtension = filePath.split('.').pop().toLowerCase();
    
    switch(mediaType) {
      case 'videos':
        return /^(mp4|mov|avi|mkv)$/i.test(fileExtension);
      case 'pdfs':
        return /^(pdf|epub|docx)$/i.test(fileExtension);
      case 'audios':
        return /^(mp3|wav|ogg|aac)$/i.test(fileExtension);
      default:
        return true;
    }
  };

  const handleMediaType = (e) => {
    const newMediaType = e.target.value;
    
    // If a file is already selected, validate it against the new media type
    if (masterFileContent && masterFileContent.filePath) {
      const isValid = validateFileType(newMediaType, masterFileContent.filePath);
      
      if (!isValid) {
        const extension = masterFileContent.filePath.split('.').pop().toLowerCase();
        setError(`Selected file (${extension}) is not compatible with ${newMediaType} media type`);
        setOpenSnackbar(true);
        return; // Don't update media type if file is incompatible
      }
    }
    
    dispatch(setMediaType(newMediaType));
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Box>
      <FormControl sx={{ my: 2 }} component="fieldset">
        <FormLabel component="legend">Select Media Type</FormLabel>
        <RadioGroup
          onChange={handleMediaType}
          aria-label="media-type"
          name="media-type"
          value={mediaType || ''}
        >
          <Box sx={{ display: "flex" }}>
            <FormControlLabel
              value="videos"
              control={<Radio />}
              label="Watchable"
            />
            <FormControlLabel 
              value="pdfs" 
              control={<Radio />} 
              label="Readable" 
            />
            <FormControlLabel
              value="audios"
              control={<Radio />}
              label="Audible"
            />
          </Box>
        </RadioGroup>
      </FormControl>
      
      {mediaType === 'videos' && (
        <Typography variant="caption" color="text.secondary">
          Supported formats: MP4, MOV, AVI, MKV
        </Typography>
      )}
      
      {mediaType === 'pdfs' && (
        <Typography variant="caption" color="text.secondary">
          Supported formats: PDF, EPUB, DOCX
        </Typography>
      )}
      
      {mediaType === 'audios' && (
        <Typography variant="caption" color="text.secondary">
          Supported formats: MP3, WAV, OGG, AAC
        </Typography>
      )}
      
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MediaTypeSelector;